/**
 * flowComponents.ts — the CONTRACT for per-component flow documentation (Will's upload flow:
 * "User has to go through each component of the flow, explaining how to set it up").
 *
 * WHY THIS LIVES IN shared/ AND NOT IN api/ OR web/:
 * both sides need the same answer to "how many components are there, what are they numbered, and
 * which ones are still unexplained". The backend derives the component list from the flow blueprint
 * (api/dataverse/components.ts — the ONLY place that touches raw `clientdata`); the frontend renders
 * one required explanation per component and gates progression on all of them being filled. If each
 * side computed "is this complete?" its own way they would drift, and the gate would be a lie on one
 * of them. So the TYPES, the NUMBERING and the VALIDATION are defined once, here, and imported by
 * both. Only the walker itself is backend-only, because only it sees the blueprint.
 *
 * NOTHING HERE TOUCHES A BLUEPRINT. Every function is a pure function of an already-derived
 * manifest, so it is safe to ship to the browser and trivially testable.
 *
 * Serves: UP-F15 (setup instructions are mandatory, not optional), UP-F16 (dependencies),
 *         Will's upload flow (per-component explanation), §23-A8/A9 (Dataverse artefact).
 */
/**
 * What kind of thing a component is, in the uploader's terms — NOT the raw Logic Apps type.
 *
 *  - `trigger` — what starts the flow. Always exactly one section, always first.
 *  - `action`  — a leaf step that does something (send an email, update a row).
 *  - `control` — a step that CONTAINS other steps (condition, loop, scope, switch). A control is
 *    itself documentable: "what is this condition testing?" is a real question an adopter needs
 *    answered, separately from the steps inside it.
 */
export const FLOW_COMPONENT_KINDS = ['trigger', 'action', 'control'];
/** Minimum characters for an explanation to count as an actual explanation rather than a keystroke. */
export const MIN_EXPLANATION_LENGTH = 10;
/** Maximum stored per component — generous, but bounded so one field cannot be a payload. */
export const MAX_EXPLANATION_LENGTH = 2000;
/**
 * Assign hierarchical numbering (`1`, `2`, `3`, `3.1`, `3.2`, `3.2.1`) across an ordered,
 * depth-first component list.
 *
 * Derived on demand rather than stored, because numbering is a function of the CURRENT structure:
 * if it were persisted in a draft it would go stale the moment the flow changed, and a stale
 * number displayed next to a live step is worse than no number.
 *
 * Returns a NEW array; the input is not mutated.
 */
export function numberComponents(components) {
    // counters[d] = how many siblings have been seen so far at depth d.
    const counters = [];
    return components.map((component) => {
        const depth = Math.max(0, component.depth);
        // Entering a shallower level discards the deeper counters — the next depth-2 run restarts at 1.
        counters.length = depth + 1;
        counters[depth] = (counters[depth] ?? 0) + 1;
        for (let d = 0; d < depth; d += 1)
            counters[d] = counters[d] ?? 1;
        return { ...component, numbering: counters.slice(0, depth + 1).join('.') };
    });
}
/** True when `value` is a real explanation: present, and not just whitespace padding. */
export function isExplained(value) {
    return typeof value === 'string' && value.trim().length >= MIN_EXPLANATION_LENGTH;
}
/**
 * The Instructions-stage gate (Will's flow: the user cannot proceed until every component is
 * explained). Returns the ids of components with a missing/whitespace-only/too-short explanation,
 * in component order, so the caller can both count them and jump to the FIRST one.
 *
 * This walks the WHOLE manifest, never only what is expanded or on screen — a collapsed section
 * with an empty explanation still blocks progression, which is the entire point of the gate.
 */
export function missingExplanations(components, explanations) {
    return components.filter((c) => !isExplained(explanations[c.id])).map((c) => c.id);
}
/** Convenience: how many components are explained, for the "7 of 10 steps explained" counter. */
export function explainedCount(components, explanations) {
    return components.filter((c) => isExplained(explanations[c.id])).length;
}
/** True when every component has an adequate explanation. The Review/Upload precondition. */
export function allExplained(components, explanations) {
    return components.length > 0 && missingExplanations(components, explanations).length === 0;
}
/**
 * Drop answers whose component no longer exists, and report what was dropped.
 *
 * Used when restoring a draft against a freshly-inspected flow: an explanation for a step the
 * author has since deleted must not be silently carried into a submission (it would document a
 * component that isn't there), but the explanations that DO still match should survive — losing a
 * builder's work because they added one action is unacceptable.
 */
export function reconcileExplanations(components, explanations) {
    const live = new Set(components.map((c) => c.id));
    const kept = {};
    const droppedIds = [];
    for (const [id, text] of Object.entries(explanations)) {
        if (live.has(id))
            kept[id] = text;
        else
            droppedIds.push(id);
    }
    return { kept, droppedIds };
}
/**
 * Pair an ordered component list with its explanations to produce the submission payload.
 * Components with no explanation are INCLUDED with whatever text exists — the caller must have
 * already gated on `allExplained`; silently dropping an unexplained component here would turn a
 * validation bug into a quietly incomplete tool record.
 */
export function toComponentDocs(components, explanations) {
    return components.map((c) => ({
        id: c.id,
        order: c.order,
        depth: c.depth,
        parentId: c.parentId,
        kind: c.kind,
        label: c.label,
        actionType: c.actionType,
        typeLabel: c.typeLabel,
        connector: c.connector,
        operation: c.operation,
        branch: c.branch,
        explanation: explanations[c.id] ?? '',
    }));
}
/**
 * Render the component docs as readable plain text.
 *
 * WHY THIS EXISTS: `app.tool.setup_instructions` is `NOT NULL CHECK (char_length >= 10)` (0004) and
 * the tool detail page already renders it. Rather than change that column's meaning, alter the
 * detail page, or leave the field to be filled in twice, the structured docs are ALSO rendered to
 * text and stored there. The structured form in `tool_external_ref` is the machine-readable
 * original; this is the human-readable projection of the same facts. Nothing is lost, and no
 * existing reader of `setup_instructions` has to change.
 */
export function renderComponentDocsAsText(docs) {
    const numbered = numberComponents(docs.map((d) => ({ ...d, numbering: '' })));
    return numbered
        .map((c, i) => {
        const indent = '  '.repeat(Math.max(0, c.depth));
        const branch = c.branch ? `${c.branch} — ` : '';
        const type = c.typeLabel ? ` [${c.typeLabel}]` : '';
        const explanation = docs[i]?.explanation?.trim() ?? '';
        return `${indent}${c.numbering}. ${branch}${c.label}${type}\n${indent}   ${explanation}`;
    })
        .join('\n\n');
}
//# sourceMappingURL=flowComponents.js.map